import * as queries from './queries';
import * as mutations from './mutations';
import { graphql } from 'services/api';
import {
  Conversation,
  ConversationStatus,
  Message,
  UnansweredQuestion
} from './types';
import { ModelSortDirection } from 'services/api/types';

export const getConversation = async ({
  id
}: {
  id: string;
}): Promise<Conversation> => {
  const response = await graphql.query(queries.getConversation, {
    id
  });

  return response.getConversation;
};

export const getConversationsByCampaign = async ({
  campaignId,
  nextToken,
  filter
}: {
  campaignId: string;
  nextToken: string | null;
  filter?: Record<string, unknown>;
}): Promise<{ items: Conversation[]; nextToken: string }> => {
  const response = await graphql.query(
    queries.getConversationsByCampaignAndUpdatedAt,
    {
      campaignId,
      nextToken,
      sortDirection: ModelSortDirection.DESC,
      limit: 500,
      filter
    }
  );

  return response.getConversationsByCampaignAndUpdatedAt;
};

export const getMessagesByCampaign = async ({
  campaignId,
  nextToken
}: {
  campaignId: string;
  nextToken: string | null;
}): Promise<{ items: Conversation[]; nextToken: string }> => {
  const response = await graphql.query(queries.messagesByCampaignId, {
    campaignId,
    nextToken,
    limit: 100
  });

  return response.messagesByCampaignId;
};

export const getMessagesByConversation = async ({
  conversationId,
  nextToken
}: {
  conversationId: string;
  nextToken: string | null;
}): Promise<{ items: Message[]; nextToken: string | null }> => {
  const response = await graphql.query(queries.getMessagesByConversation, {
    conversationId,
    nextToken,
    // sortDirection: 'DESC',
    limit: 200
  });

  return response.getMessagesByConversation;
};

export const sendMessageByUser = async ({
  messageBody,
  conversationId
}: {
  conversationId: string;
  messageBody: string;
}): Promise<{ message: Message }> => {
  const response = await graphql.mutation(mutations.sendMessageByUser, {
    messageBody,
    conversationId
  });

  return response.sendMessageByUser;
};

export const updateConversation = async ({
  id,
  isRead,
  isHumanTakeover
}: {
  id: string;
  isHumanTakeover?: boolean;
  isRead?: boolean;
}): Promise<Conversation> => {
  const response = await graphql.mutation(mutations.updateConversation, {
    id,
    isHumanTakeover,
    isRead
  });

  return response.updateConversation;
};

export const restartConversation = async (
  conversationId: string
): Promise<Conversation> => {
  const response = await graphql.mutation(mutations.restartConversation, {
    conversationId
  });

  return response.restartConversation;
};

export const getDifferentConversationOngoing = async ({
  twilioConversationId,
  conversationId
}: {
  twilioConversationId: string;
  conversationId: string;
}) => {
  const response = await graphql.query(
    queries.getConversationsByStatusAndTwilioConversationId,
    {
      status: ConversationStatus.ONGOING,
      twilioConversationId: {
        eq: twilioConversationId
      },
      filter: {
        id: {
          ne: conversationId
        }
      }
    }
  );

  return response.getConversationsByStatusAndTwilioConversationId.items[0];
};

export const getAttachmentsByConversation = async ({
  conversationId
}: {
  conversationId: string;
}) => {
  const response = await graphql.query(queries.getAttachmentsByConversation, {
    conversationId,
    limit: 100
  });

  return response.getAttachmentsByConversation;
};

export const getUnansweredQuestionsByConversation = async ({
  conversationId
}: {
  conversationId: string;
}) => {
  const response = await graphql.query(
    queries.getUnansweredQuestionsByConversation,
    {
      conversationId,
      limit: 50
    }
  );

  return response.getUnansweredQuestionsByConversation.items.sort(
    (a: UnansweredQuestion, b: UnansweredQuestion) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
};

export const updateUnansweredQuestion = async ({
  id,
  isAnswered
}: {
  id: string;
  isAnswered: boolean;
}): Promise<UnansweredQuestion> => {
  const response = await graphql.mutation(mutations.updateUnansweredQuestion, {
    id,
    isAnswered
  });

  return response.updateUnansweredQuestion;
};
